import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus, MagnifyingGlass, Eye, PencilSimple, Lock, Buildings, Users, MapPin, CheckCircle, XCircle,
} from '@phosphor-icons/react';
import api from '@/lib/api';

const STATES = [
  { code: 'DL', name: 'Delhi', gstin_code: '07' },
  { code: 'HR', name: 'Haryana', gstin_code: '06' },
  { code: 'UP', name: 'Uttar Pradesh', gstin_code: '09' },
  { code: 'KA', name: 'Karnataka', gstin_code: '29' },
  { code: 'PB', name: 'Punjab', gstin_code: '03' },
  { code: 'CH', name: 'Chandigarh', gstin_code: '04' },
  { code: 'RJ', name: 'Rajasthan', gstin_code: '08' },
  { code: 'MH', name: 'Maharashtra', gstin_code: '27' },
  { code: 'TN', name: 'Tamil Nadu', gstin_code: '33' },
  { code: 'TS', name: 'Telangana', gstin_code: '36' },
  { code: 'GJ', name: 'Gujarat', gstin_code: '24' },
  { code: 'WB', name: 'West Bengal', gstin_code: '19' },
];

export default function ClinicsMasterPage() {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editClinic, setEditClinic] = useState(null);
  const [viewClinic, setViewClinic] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: '', code: '', address: '', state: '', state_code: '', gstin: '', password: 'hearclear@clinic',
  });

  const loadClinics = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/clinic/admin/list');
      setClinics(data.clinics || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadClinics(); }, [loadClinics]);

  const resetForm = () => setForm({ name: '', code: '', address: '', state: '', state_code: '', gstin: '', password: 'hearclear@clinic' });

  const handleCreate = async () => {
    if (!form.name || !form.code) { toast.error('Name and Code are required'); return; }
    setSaving(true);
    try {
      await api.post('/clinic/admin/create', form);
      toast.success(`Clinic ${form.name} created!`);
      setShowCreate(false);
      resetForm();
      loadClinics();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create clinic');
    } finally { setSaving(false); }
  };

  const handleUpdate = async () => {
    if (!editClinic) return;
    setSaving(true);
    try {
      await api.put(`/clinic/admin/${editClinic.id}`, {
        name: form.name || undefined,
        code: form.code || undefined,
        address: form.address || undefined,
        state: form.state || undefined,
        state_code: form.state_code || undefined,
        gstin: form.gstin || undefined,
        is_active: form.is_active,
      });
      toast.success('Clinic updated!');
      setEditClinic(null);
      loadClinics();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally { setSaving(false); }
  };

  const handleResetPassword = async (clinicId) => {
    try {
      const { data } = await api.post(`/clinic/admin/${clinicId}/reset-password`);
      toast.success(`Password reset to: ${data.new_password}`);
    } catch (e) {
      toast.error('Failed to reset password');
    }
  };

  const openEdit = (clinic) => {
    setForm({
      name: clinic.name, code: clinic.code, address: clinic.address || '',
      state: clinic.state || '', state_code: clinic.state_code || '',
      gstin: clinic.gstin || '', is_active: clinic.is_active !== false,
    });
    setEditClinic(clinic);
  };

  const selectState = (stateName) => {
    const s = STATES.find(st => st.name === stateName);
    setForm(f => ({ ...f, state: stateName, state_code: s?.code || '' }));
  };

  const filtered = search
    ? clinics.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()) || c.code?.toLowerCase().includes(search.toLowerCase()))
    : clinics;

  const activeCount = clinics.filter(c => c.is_active !== false).length;
  const totalEmployees = clinics.reduce((sum, c) => sum + (c.employee_count || 0), 0);

  return (
    <div className="p-6" data-testid="clinics-master-page">
      <PageHeader title="Clinics Master" subtitle="Manage clinic locations, accounts, and HR allocations" />

      {/* Summary */}
      <div className="mt-6 grid grid-cols-3 gap-4">
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="clinics-total-card">
          <div className="flex items-center gap-2 mb-1"><Buildings size={16} className="text-[#0F4C81]" /><span className="text-xs text-[#64748B]">Total Clinics</span></div>
          <p className="text-2xl font-bold text-[#0F4C81]">{clinics.length}</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="clinics-active-card">
          <div className="flex items-center gap-2 mb-1"><CheckCircle size={16} className="text-green-600" /><span className="text-xs text-[#64748B]">Active</span></div>
          <p className="text-2xl font-bold text-green-600">{activeCount}</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4" data-testid="clinics-employees-card">
          <div className="flex items-center gap-2 mb-1"><Users size={16} className="text-blue-600" /><span className="text-xs text-[#64748B]">Total Employees</span></div>
          <p className="text-2xl font-bold text-blue-600">{totalEmployees}</p>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-4 flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clinics..." className="pl-9" data-testid="clinics-search" />
        </div>
        <Button size="sm" className="bg-[#0F4C81] text-white hover:bg-[#0B3C68] ml-auto" onClick={() => { resetForm(); setShowCreate(true); }} data-testid="clinics-create-btn">
          <Plus size={14} className="mr-1" /> Add Clinic
        </Button>
      </div>

      {/* Table */}
      <div className="mt-4 bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="clinics-table">
          <thead>
            <tr className="bg-[#F8FAFC] text-[#64748B] text-xs font-medium">
              <th className="text-left p-3">Code</th>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">State</th>
              <th className="text-left p-3">GSTIN</th>
              <th className="text-center p-3">Employees</th>
              <th className="text-center p-3">Status</th>
              <th className="p-3 w-[120px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="text-center py-8 text-[#64748B]">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-8 text-[#64748B]">No clinics found</td></tr>
            ) : filtered.map(c => (
              <tr key={c.id} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`clinic-row-${c.code}`}>
                <td className="p-3 font-mono font-semibold text-[#0F4C81]">{c.code}</td>
                <td className="p-3">
                  <div>{c.name}</div>
                  {c.is_corporate && <span className="text-[10px] bg-purple-50 text-purple-700 px-1.5 py-0.5 rounded-full">Corporate</span>}
                </td>
                <td className="p-3 text-xs">{c.state || '-'} <span className="text-[#64748B]">({c.state_code || '-'})</span></td>
                <td className="p-3 text-xs font-mono">{c.gstin || '-'}</td>
                <td className="p-3 text-center">
                  <span className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">{c.employee_count || 0}</span>
                </td>
                <td className="p-3 text-center">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${c.is_active !== false ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-600'}`}>
                    {c.is_active !== false ? 'Active' : 'Closed'}
                  </span>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setViewClinic(c)} data-testid={`clinic-view-${c.code}`}>
                      <Eye size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(c)} data-testid={`clinic-edit-${c.code}`}>
                      <PencilSimple size={14} />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-amber-600" onClick={() => handleResetPassword(c.id)} data-testid={`clinic-reset-${c.code}`}>
                      <Lock size={14} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* View Detail Dialog */}
      <Dialog open={!!viewClinic} onOpenChange={() => setViewClinic(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Buildings size={18} /> {viewClinic?.name}</DialogTitle></DialogHeader>
          {viewClinic && (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-[#64748B]">Code:</span> <strong>{viewClinic.code}</strong></div>
                <div><span className="text-[#64748B]">Username:</span> <strong>{viewClinic.username}</strong></div>
                <div><span className="text-[#64748B]">State:</span> {viewClinic.state || '-'} ({viewClinic.state_code || '-'})</div>
                <div><span className="text-[#64748B]">GSTIN:</span> {viewClinic.gstin || '-'}</div>
                <div><span className="text-[#64748B]">Employees:</span> {viewClinic.employee_count || 0}</div>
                <div><span className="text-[#64748B]">Status:</span> {viewClinic.is_active !== false ? 'Active' : 'Closed'}</div>
              </div>
              {viewClinic.address && <p className="text-xs"><span className="text-[#64748B]">Address:</span> {viewClinic.address}</p>}
              <p className="text-[10px] text-[#64748B]">Login: username={viewClinic.username}, default password=hearclear@clinic</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add New Clinic</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Clinic Name *</label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="HearClear XYZ" data-testid="clinic-form-name" />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Code * (username for login)</label>
                <Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="XYZ" data-testid="clinic-form-code" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">State</label>
                <Select value={form.state} onValueChange={selectState}>
                  <SelectTrigger data-testid="clinic-form-state"><SelectValue placeholder="Select state..." /></SelectTrigger>
                  <SelectContent>{STATES.map(s => <SelectItem key={s.code} value={s.name}>{s.name} ({s.code})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">GSTIN</label>
                <Input value={form.gstin} onChange={e => setForm(f => ({ ...f, gstin: e.target.value }))} placeholder="07AAACH..." data-testid="clinic-form-gstin" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Address</label>
              <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} placeholder="Full address" data-testid="clinic-form-address" />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Password (default: hearclear@clinic)</label>
              <Input value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} data-testid="clinic-form-password" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={saving} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="clinic-create-submit">
                {saving ? 'Creating...' : 'Create Clinic'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editClinic} onOpenChange={() => setEditClinic(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit Clinic: {editClinic?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Clinic Name</label>
                <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="clinic-edit-name" />
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">Code</label>
                <Input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} data-testid="clinic-edit-code" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#64748B] block mb-1">State</label>
                <Select value={form.state} onValueChange={selectState}>
                  <SelectTrigger data-testid="clinic-edit-state"><SelectValue placeholder="Select state..." /></SelectTrigger>
                  <SelectContent>{STATES.map(s => <SelectItem key={s.code} value={s.name}>{s.name} ({s.code})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs text-[#64748B] block mb-1">GSTIN</label>
                <Input value={form.gstin} onChange={e => setForm(f => ({ ...f, gstin: e.target.value }))} data-testid="clinic-edit-gstin" />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Address</label>
              <Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} data-testid="clinic-edit-address" />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-[#64748B]">Status:</label>
              <Select value={form.is_active ? 'active' : 'closed'} onValueChange={v => setForm(f => ({ ...f, is_active: v === 'active' }))}>
                <SelectTrigger className="w-[140px]" data-testid="clinic-edit-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setEditClinic(null)}>Cancel</Button>
              <Button onClick={handleUpdate} disabled={saving} className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" data-testid="clinic-edit-submit">
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
